import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const autoLoginUser = async (email, name) => {
    try {
      const response = await axios.post(`${API}/user/auto-login`, { email });
      
      localStorage.setItem('user_token', response.data.token);
      localStorage.setItem('user_email', response.data.email);
      localStorage.setItem('user_name', response.data.name);
      
      // Clear checkout data
      localStorage.removeItem('checkout_email');
      localStorage.removeItem('checkout_name');
      
      toast.success('Logged in! Redirecting to your dashboard...');
      setTimeout(() => {
        navigate('/user/dashboard');
      }, 2000);
    } catch (error) {
      console.error('Auto-login failed:', error);
      toast.info('Please login to view your account');
      setTimeout(() => {
        navigate('/user/login');
      }, 2000);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPaymentDetails(data);
        
        // Auto-login user to view their dashboard
        const userEmail = localStorage.getItem('checkout_email');
        const userName = localStorage.getItem('checkout_name');
        if (userEmail) {
          // Auto-login
          autoLoginUser(userEmail, userName);
        }
        
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('failed');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
    }
  };

  return (
    <div data-testid="payment-success-page" className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl bg-white/95 backdrop-blur-sm">
        <CardHeader className="text-center space-y-4">
          {paymentStatus === 'checking' && (
            <>
              <div className="flex justify-center">
                <Loader2 className="w-16 h-16 text-indigo-600 animate-spin" />
              </div>
              <CardTitle className="text-2xl font-bold">Verifying Payment</CardTitle>
              <CardDescription>Please wait while we confirm your payment...</CardDescription>
            </>
          )}

          {paymentStatus === 'success' && (
            <>
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-green-600">Payment Successful!</CardTitle>
              <CardDescription>
                Your subscription has been activated. A confirmation email with your invoice has been sent to your email address.
              </CardDescription>
            </>
          )}

          {(paymentStatus === 'failed' || paymentStatus === 'error' || paymentStatus === 'timeout') && (
            <>
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-12 h-12 text-red-600" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-red-600">Payment Failed</CardTitle>
              <CardDescription>
                {paymentStatus === 'timeout'
                  ? 'Payment verification timed out. Please check your email for confirmation.'
                  : 'There was an issue processing your payment. Please try again.'}
              </CardDescription>
            </>
          )}
        </CardHeader>

        <CardContent className="space-y-4">
          {paymentDetails && paymentDetails.amount_total && (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount:</span>
                <span className="font-semibold">${((paymentDetails.amount_total || 0) / 100).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Currency:</span>
                <span className="font-semibold">{paymentDetails.currency ? paymentDetails.currency.toUpperCase() : 'USD'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-semibold text-green-600">Paid</span>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Button
              data-testid="back-to-packages-button"
              onClick={() => navigate('/packages')}
              className="w-full h-12 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              Back to Packages
            </Button>
            {paymentStatus === 'success' && (
              <p className="text-center text-sm text-gray-500">
                Check your email for your payment receipt and subscription details.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
